
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useSecureAdminAuth } from "@/hooks/useSecureAdminAuth";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RefreshCw, Calendar, DollarSign, User, Package, Search, Copy, Phone, Globe } from "lucide-react";

interface Purchase {
  id: string;
  user_id: string;
  amount: number; // Keep for backward compatibility
  amount_usd: number;
  amount_inr: number;
  currency: string;
  status: string;
  purchase_type: string;
  payment_from: string;
  start_date: string;
  end_date: string;
  created_at: string;
  razorpay_order_id: string | null;
  razorpay_payment_id: string | null;
  razorpay_signature: string | null;
  profiles?: {
    email: string;
    full_name: string | null;
    phone_number: string | null;
    country_code: string | null;
    country_name: string | null;
  };
}

const PurchaseHistoryManager = () => {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [filteredPurchases, setFilteredPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [premiumPriceINR, setPremiumPriceINR] = useState<number>(299);
  const { toast } = useToast();
  const { adminUser } = useSecureAdminAuth();

  // Fetch premium price from printables settings
  const fetchPremiumPrice = async () => {
    try {
      const { data, error } = await supabase
        .from('printables_settings')
        .select('setting_value')
        .eq('setting_key', 'premium_price_inr')
        .single();

      if (error) {
        console.error('Error fetching premium price:', error);
        return;
      }

      if (data?.setting_value) {
        const price = parseFloat(data.setting_value);
        if (!isNaN(price)) {
          setPremiumPriceINR(price);
        }
      }
    } catch (error) {
      console.error('Error fetching premium price:', error);
    }
  };

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      console.log('Fetching purchases with profiles via edge function...');
      
      // Use the admin edge function to get purchases with user data
      // Send the session token header AND the admin identity as a fallback,
      // so a missing/expired local session cannot break data loading.
      const adminSessionToken =
        typeof window !== "undefined"
          ? localStorage.getItem("admin_session_token") || ""
          : "";
      const { data, error } = await supabase.functions.invoke('admin-get-users', {
        headers: { "x-admin-session-token": adminSessionToken },
        body: {
          action: 'get_purchases_with_users',
          adminUser: adminUser
            ? { username: adminUser.username, email: adminUser.email }
            : undefined
        }
      });

      console.log('Purchase data from edge function:', data);
      console.log('Purchase error:', error);

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }
      
      setPurchases(data?.purchases || []);
      setFilteredPurchases(data?.purchases || []);
    } catch (error: any) {
      console.error('Error fetching purchases:', error);
      toast({
        title: "Error",
        description: "Failed to fetch purchase history: " + (error.message || 'Unknown error'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await fetchPremiumPrice();
      await fetchPurchases();
    };
    initializeData();
  }, []);

  useEffect(() => {
    const filtered = purchases.filter(purchase =>
      // Search by user details
      purchase.profiles?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchase.profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchase.profiles?.phone_number?.includes(searchTerm) ||
      purchase.profiles?.country_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      // Search by user ID
      purchase.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      // Search by payment details
      purchase.razorpay_order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchase.razorpay_payment_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchase.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPurchases(filtered);
  }, [searchTerm, purchases]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: "Copied!",
        description: `${label} copied to clipboard`,
      });
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmountUSD = (purchase: Purchase) => {
    return purchase.amount_usd > 0 ? `$${purchase.amount_usd.toFixed(2)}` : '-';
  };

  const formatAmountINR = (purchase: Purchase) => {
    return purchase.amount_inr > 0 ? `₹${purchase.amount_inr.toFixed(2)}` : '-';
  };

  const formatPhoneNumber = (phoneNumber: string | null, countryCode: string | null) => {
    if (!phoneNumber) return '-';
    return `${countryCode || ''}${phoneNumber}`;
  };

  const getPaymentGateway = (purchase: Purchase) => {
    // For purchases with razorpay_order_id, always show "Razorpay" regardless of payment_from
    if (purchase.razorpay_order_id) {
      return 'Razorpay';
    }
    
    // For other purchases, show the actual payment gateway
    return purchase.payment_from === 'by admin' ? 'Admin' : 'Razorpay';
  };

  const getStatusBadge = (purchase: Purchase) => {
    const statusColors = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
    };
    
    // For purchases with razorpay_order_id, always show "Razorpay" as gateway
    const displayPaymentFrom = purchase.razorpay_order_id ? 'Razorpay' : 
                              (purchase.payment_from === 'by admin' ? 'Admin' : 'Razorpay');
    
    return (
      <div className="space-y-1">
        <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[purchase.status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'}`}>
          {purchase.status?.charAt(0).toUpperCase() + purchase.status?.slice(1) || 'Unknown'}
        </span>
        <div className="text-xs text-gray-500">
          Gateway: {displayPaymentFrom}
        </div>
      </div>
    );
  };

  const getTotalStats = () => {
    const totalRevenueUSD = purchases.reduce((sum, p) => {
      return p.status === 'completed' ? sum + (p.amount_usd || 0) : sum;
    }, 0);
    
    const totalRevenueINR = purchases.reduce((sum, p) => {
      return p.status === 'completed' ? sum + (p.amount_inr || 0) : sum;
    }, 0);
  
    const completedPurchases = purchases.filter(p => p.status === 'completed');
  
    const activePurchases = completedPurchases.filter(p => {
      if (!p.end_date) return false;
      return new Date(p.end_date) > new Date();
    }).length;
  
    return {
      totalPurchases: purchases.length,
      completedPurchases: completedPurchases.length,
      totalRevenueUSD,
      totalRevenueINR,
      activePurchases,
    };
  };
 
  
  const stats = getTotalStats();

  return (
    <div className="space-y-6">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div>
        <div>
          <h3 className="text-2xl font-bold text-slate-800">Purchase History</h3>
          <p className="text-slate-600">View all customer purchases and payment details (Original Razorpay data - unaffected by admin changes)</p>
        </div>
        <Button onClick={fetchPurchases} disabled={loading} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Purchases</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalPurchases}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <User className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.completedPurchases}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue (USD)</CardTitle>
            <DollarSign className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              ${stats.totalRevenueUSD.toFixed(2)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue (INR)</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ₹{stats.totalRevenueINR.toFixed(2)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
            <Calendar className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{stats.activePurchases}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2">
            <Search className="w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by username, user ID, email, phone, payment ID, or Razorpay order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-lg"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Purchase History ({filteredPurchases.length} purchases)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[120px]">User ID</TableHead>
                    <TableHead className="min-w-[200px]">Customer Details</TableHead>
                    <TableHead className="min-w-[150px]">Contact & Country</TableHead>
                    <TableHead className="min-w-[180px]">Payment ID</TableHead>
                    <TableHead className="min-w-[100px]">Amount (USD)</TableHead>
                    <TableHead className="min-w-[100px]">Amount (INR)</TableHead>
                    <TableHead className="min-w-[120px]">Payment Gateway</TableHead>
                    <TableHead className="min-w-[150px]">Purchase Date</TableHead>
                    <TableHead className="min-w-[150px]">Start Date</TableHead>
                    <TableHead className="min-w-[150px]">End Date</TableHead>
                    <TableHead className="min-w-[150px]">Status & Gateway</TableHead>
                    <TableHead className="min-w-[120px]">Type</TableHead>
                    <TableHead className="min-w-[200px]">Razorpay Order ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPurchases.map((purchase) => (
                    <TableRow key={purchase.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                            {purchase.user_id.substring(0, 8)}...
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(purchase.user_id, "User ID")}
                            className="h-6 w-6 p-0"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {purchase.profiles?.full_name || 'Unknown User'}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-2">
                            {purchase.profiles?.email || 'No email'}
                            {purchase.profiles?.email && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyToClipboard(purchase.profiles?.email || '', "Email")}
                                className="h-4 w-4 p-0"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {purchase.profiles?.phone_number ? (
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="w-3 h-3" />
                              <span>{formatPhoneNumber(purchase.profiles.phone_number, purchase.profiles.country_code)}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyToClipboard(formatPhoneNumber(purchase.profiles?.phone_number, purchase.profiles?.country_code), "Phone")}
                                className="h-4 w-4 p-0"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">No phone</span>
                          )}
                          {purchase.profiles?.country_name && (
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              <Globe className="w-3 h-3" />
                              {purchase.profiles.country_name}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {purchase.razorpay_payment_id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono bg-blue-100 px-2 py-1 rounded">
                              {purchase.razorpay_payment_id.substring(0, 15)}...
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(purchase.razorpay_payment_id || '', "Payment ID")}
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatAmountUSD(purchase)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatAmountINR(purchase)}
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {getPaymentGateway(purchase)}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(purchase.created_at)}</TableCell>
                      <TableCell>{formatDate(purchase.start_date)}</TableCell>
                      <TableCell>
                        <div className={`${purchase.end_date && new Date(purchase.end_date) < new Date() ? 'text-red-600' : 'text-green-600'}`}>
                          {formatDate(purchase.end_date)}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(purchase)}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                          {purchase.purchase_type || 'printables_premium'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {purchase.razorpay_order_id ? (
                          <div className="flex items-center gap-2">
                            <span className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                              {purchase.razorpay_order_id.substring(0, 20)}...
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(purchase.razorpay_order_id || '', "Razorpay Order ID")}
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {filteredPurchases.length === 0 && !loading && (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? 'No purchases found matching your search.' : 'No purchases found'}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchaseHistoryManager;
